import React, { useState, useEffect } from 'react';
import CurrencyFormat from 'react-currency-format';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { Creators as actions } from '../../redux/actions';
import {
    getFinanceOptions,
    getIsLeaseSelected,
    getMetaFinancePlans,
    getSelectedFinancePlan,
    getStateFinance,
    getSelectedFinanceOptions,
    getHasTaxesIncluded,
    getPaymentOptions,
    getMoneyFormatter,
    getQuote
} from '../../redux/selectors';
import { t } from 'react-i18nify';
import { paymentWithTaxDetails } from '../../libs/helpers';
import GetTradeinEstimate from '../GetTradeinEstimate';
import SelectKmPlan from '../SelectKmPlan';
import { PAYMENT_ADJUSTMENT_TYPE } from '../../constants/payment';

const LeaseFinanceForm = () => {
    const financeOptions = useSelector(getSelectedFinanceOptions);
    const paymentOptions = useSelector(getPaymentOptions);
    const selectedFinancePlan = useSelector(getSelectedFinancePlan);
    const formatAsMoney = useSelector(getMoneyFormatter);
    const isLeaseSelected = useSelector(getIsLeaseSelected);
    const quote = useSelector(getQuote);

    const dispatch = useDispatch();

    const { paymentFrequency, selectedPayment, selectedTerm, selectedApr, isAvailable, kmPerYearPlan, buyBackAmount } = financeOptions || {};
    const { paymentFrequencies } = selectedFinancePlan || {};

    const { payment, paymentTaxes, taxesDetail, totalPayment } = selectedPayment || {};
    const { lien, tradeIn, cashDown } = paymentOptions || {};
    const { finance } = quote || {};
    const { tradeInTaxCreditsDetails } = finance || {};

    const [currentPaymentValue, setPaymentValue] = useState(payment);
    const [currentCashDownValue, setCashDownValue] = useState(cashDown);

    useEffect(() => {
        setPaymentValue(payment);
        setCashDownValue(cashDown);
    }, [payment, cashDown]);

    const _term = `<span class="vehicle-finance__item-value-term">${t('financeTermMonth', { termMonths: selectedTerm })}</span>`;
    const _apr = `<span class="vehicle-finance__item-value-apr">${selectedApr}%</span>`;
    const _kmplan = `<span class="vehicle-finance__item-value-kmplan">${finance?.term?.kmPerYearPlanDetails?.totalKmPerYear} km</span>`;
    const _cashdown = `<span class="vehicle-finance__item-value-cashdown">${formatAsMoney(cashDown)}</span>`;
    const _tradein = `<span class="vehicle-finance__item-value-tradein">${formatAsMoney(tradeIn)}</span>`;
    const _lien = `<span class="vehicle-finance__item-value-lien">${formatAsMoney(lien)}</span>`;
    const financeTermCashdown = t('financeTerm', {term: _term, apr: _apr, cashdown: _cashdown, tradein: _tradein, lien: _lien});
    const leaseTermCashdown = t('leaseTerm', {term: _term, apr: _apr, cashdown: _cashdown, kmplan: _kmplan, tradein: _tradein, lien: _lien});
    const termAprCashdown = isLeaseSelected ? leaseTermCashdown : financeTermCashdown;
    // const termAprCashdown = t('financeTerm', {term: _term, apr: _apr, cashdown: _cashdown, tradein: _tradein, lien: _lien});
    const defaultMaskOptions = {
        prefix: '$ ',
        suffix: '',
        includeThousandsSeparator: true,
        thousandsSeparatorSymbol: ',',
        allowDecimal: true,
        decimalSymbol: '.',
        decimalLimit: 2, // how many digits allowed after the decimal
        integerLimit: 7, // limit length of integer numbers
        allowNegative: false,
        allowLeadingZeroes: false,
    };

    const currencyMask = createNumberMask(defaultMaskOptions);
    const onChangeTradeInAmount = debounce(value => dispatch(actions.changeTradeInAmount(value.replace(/[$,]+/g,""))), 400);
    const onChangeLienAmount = debounce(value => dispatch(actions.changeLienAmount(value.replace(/[$,]+/g,""))), 400);

    const onChangeCashdownAmount = value => {
        setCashDownValue(value.replace(/[$,]+/g,""));
        dispatch(actions.changeCashDownAmount(value.replace(/[$,]+/g,"")));
    }

    const onChangeDesiredPayment = value => {
        setPaymentValue(value.replace(/[$,]+/g,""));
        dispatch(actions.changeDesiredPayment(PAYMENT_ADJUSTMENT_TYPE.CASH_DOWN, value.replace(/[$,]+/g,"")));
    }

    return (
        <>
            {!isAvailable && "message d'erreur"}

            { isAvailable &&
            <div className="vehicle-payment-tabs__content-item">
                <div className="calculator-popup-table-finance-lease">
                    <div className="calculator-popup-table-finance-lease__elem">
                        <div className="calculator-popup-table-finance-lease__payment-selected">
                            <div className="calculator-popup-table-finance-lease__payment-selected-frequency">
                                <span className="vehicle-finance__item-value text-bold" data-theme-style="vehicleFinance_termPayment_fontColor">{formatAsMoney(payment)} </span>
                                <span className="vehicle-finance__item-value">/{t(`paymentFrequency.${paymentFrequency}`)}</span>
                            </div>
                            <div className="calculator-popup-table-finance-lease__payment-selected-termAprCashdown">
                                <span className="vehicle-finance__item-value" data-theme-style="vehicleFinance_term_fontColor" dangerouslySetInnerHTML={{ __html: termAprCashdown }}/>
                            </div>
                            <div className="calculator-popup-table-finance-lease__payment-selected-paymentDetail">
                                <span className="smallprint" data-theme-style="smallprint">
                                    {paymentWithTaxDetails(formatAsMoney(payment), formatAsMoney(paymentTaxes), taxesDetail, formatAsMoney(totalPayment))}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="calculator-popup-table-finance-lease__elem">
                        <div className="calculator-popup-table-finance-lease__item">
                            <span className="calculator-popup-table-finance-lease__item-label">{t('financeTargetPayment')}</span>
                            <span className="calculator-popup-table-finance-lease__item-value">
                                <MaskedInput
                                    value={currentPaymentValue > 0 ? currentPaymentValue : ''}
                                    mask={currencyMask}
                                    className="input-number"
                                    type="text"
                                    placeholder="$ 0"
                                    onChange={e => onChangeDesiredPayment(e.target.value)}
                                />
                            </span>
                        </div>
                    </div>
                    <div className="calculator-popup-table-finance-lease__elem">
                        <div className="calculator-popup-table-finance-lease__item">
                            <span className="calculator-popup-table-finance-lease__item-label">{t('financePaymentFrequency')}</span>
                            <span className="calculator-popup-table-finance-lease__item-value">
                                <i className="select-icon" role="none" data-theme-sprite="simple-arrow-down"></i>
                                <select name="paymentFrequency"
                                        id="paymentFrequency"
                                        value={paymentFrequency}
                                        onChange={e => dispatch(actions.changePaymentFrequency(e.target.value))}>
                                    {
                                        paymentFrequencies && paymentFrequencies.map(frequency => (
                                            <option value={frequency}>{t(`paymentFrequency.${frequency}`)}</option>
                                        ))
                                    }
                                </select>
                            </span>
                        </div>
                    </div>
                    <div className="calculator-popup-table-finance-lease__elem">
                        <div className="calculator-popup-table-finance-lease__item">
                            <span className="calculator-popup-table-finance-lease__item-label">{t('financeCashdown')}</span>
                            <span className="calculator-popup-table-finance-lease__item-value">
                                <MaskedInput
                                    value={currentCashDownValue > 0 ? currentCashDownValue : ''}
                                    mask={currencyMask}
                                    className="input-number"
                                    placeholder="$ 0"
                                    type="text"
                                    onChange={e => onChangeCashdownAmount(e.target.value)}
                                />
                            </span>
                        </div>
                    </div>
                    { isLeaseSelected && (
                        <div className="calculator-popup-table-finance-lease__elem">
                            <div className="calculator-popup-table-finance-lease__item">
                                <span className="calculator-popup-table-finance-lease__item-label">{t('financeKilometerPlan')}</span>
                                <span className="calculator-popup-table-finance-lease__item-value">
                                    <i className="select-icon" role="none" data-theme-sprite="simple-arrow-down"></i>
                                    <SelectKmPlan />
                                </span>
                            </div>
                        </div>
                    )}
                    <div className="calculator-popup-table-finance-lease__elem">
                        <div className="calculator-popup-table-finance-lease__item">
                            <span className="calculator-popup-table-finance-lease__item-label">{t('financeTradeinEstimate')}</span>
                            <span className="calculator-popup-table-finance-lease__item-value">
                                <MaskedInput
                                    defaultValue={tradeIn > 0 ? tradeIn : ''}
                                    mask={currencyMask}
                                    className="input-number"
                                    placeholder="$ 0"
                                    type="text"
                                    onChange={e => onChangeTradeInAmount(e.target.value)}
                                />
                            </span>
                        </div>
                    </div>
                    <div className="calculator-popup-table-finance-lease__elem">
                        <div className="calculator-popup-table-finance-lease__tradein-details">
                            {(tradeInTaxCreditsDetails.tradeIn > 0) &&
                                <span className="calculator-popup-table-finance-lease__tradein-details-taxes" data-theme-style="smallprint">
                                    + {formatAsMoney(tradeInTaxCreditsDetails.tradeInTaxCredits)} ({t('taxCredit')}) = {formatAsMoney(tradeInTaxCreditsDetails.totalTradeIn)}
                                </span>
                            }
                            <GetTradeinEstimate />
                        </div>

                        <div className="calculator-popup-table-finance-lease__item">
                            <span className="calculator-popup-table-finance-lease__item-label">{t('financeAmountOwing')}</span>
                            <span className="calculator-popup-table-finance-lease__item-value">
                                <MaskedInput
                                    defaultValue={lien > 0 ? lien : ''}
                                    mask={currencyMask}
                                    className="input-number"
                                    placeholder="$ 0"
                                    type="text"
                                    onChange={e => onChangeLienAmount(e.target.value)}
                                />
                            </span>
                        </div>
                    </div>
                    { isLeaseSelected && buyBackAmount && (
                        <div className="calculator-popup-table-finance-lease__elem calculator-popup-table-finance-lease__elem--padding-top display-none">
                            <div className="calculator-popup-table-finance-lease__item">
                                <span className="calculator-popup-table-finance-lease__item-label">{t('financeBuyBackValue')}</span>
                                <span className="calculator-popup-table-finance-lease__item-value">
                                    {formatAsMoney(buyBackAmount)}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            }
        </>
    )
};

export default LeaseFinanceForm
